import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Layout from "../../Component/Layout";
import { ORDER_API, MASTER_API } from "../../apiConfig";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { Link, useLocation } from "react-router-dom";
import { useFieldPermissions } from "../../utils/useFieldPermissions";
import MaskedValue from "../../Component/MaskedValue";
import GoodsIssueForm from "./Goodsissueform";
import { FileText } from 'lucide-react';
import { authHeaders } from "../../utils/authHeaders";

// Reads module-level permissions (can_view / can_create / can_edit /
// can_convert) for OUTBOUND_GOODS_ISSUE from the logged-in user's stored
// permissions — same array authController.js returns at login, same
// pattern Confirmation.js / GoodsRequest.js / ReworkOrder.js use. Admins
// get an empty `permissions` array from the backend by design, so they're
// granted full access here rather than being locked out by an "empty
// permissions" default.
//
// Goods Issue only ever needs two of the four flags:
//  - canView -> DISPLAY: browse both tabs (Active, Issued) & the View/Docs
//               modal — always available.
//  - canEdit -> CHANGE: on the Active tab, select rows and Issue them, or
//               Close an open request line (the "Issue" and "Close"
//               actions/buttons).
// There is no separate Create or Convert step here, so canCreate/canConvert
// are read but intentionally unused.
const getModulePermissions = (moduleId) => {
  try {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) return { canView: false, canCreate: false, canEdit: false, canConvert: false };
    if (user.role === "admin") return { canView: true, canCreate: true, canEdit: true, canConvert: true };

    const perm = (user.permissions || []).find((p) => p.module_id === moduleId);
    return {
      canView: perm?.can_view === 1,
      canCreate: perm?.can_create === 1,
      canEdit: perm?.can_edit === 1,
      canConvert: perm?.can_convert === 1
    };
  } catch (e) {
    console.error("Error reading user permissions:", e);
    return { canView: false, canCreate: false, canEdit: false, canConvert: false };
  }
};

// ══════════════════════════════════════════════════════════
// getCurrentUserId
// Returns the logged-in user's ID (never name/email) — same
// robust fallback chain as GoodsRequest.js's getCurrentUserId,
// so this works regardless of exactly which key the login
// response uses for the user's ID.
// ══════════════════════════════════════════════════════════
const getCurrentUserId = () => {
  const userObj = localStorage.getItem("user");
  if (userObj) {
    try {
      const parsedUser = JSON.parse(userObj);
      const id = parsedUser.userId ?? parsedUser.user_id ?? parsedUser.id;
      if (id !== undefined && id !== null && id !== "") return id;
    } catch (e) {
      console.error("Error parsing user data:", e);
    }
  }
  const storedUserId = localStorage.getItem("userId");
  if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
  return "";
};

// ── Returns today's date as "YYYY-MM-DD" using LOCAL time components
//    (no UTC conversion). `new Date().toISOString()` converts to UTC
//    first, which rolls back to the previous day for any local time
//    before UTC — this is what was causing the issue date to go one
//    day back. Used for the native <input type="date"> default value.
const getLocalISODate = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// ── Converts a "YYYY-MM-DD" (optionally with a "THH:MM:SS" suffix)
//    date string into "DD-MM-YYYY" for display purposes only.
//    Pure string manipulation (no `new Date(...)` involved) so there is
//    no UTC/local timezone conversion and the date can never shift by
//    a day. Anything that isn't a recognisable date is returned as-is.
const toDMY = (value) => {
  if (!value) return value;
  const datePart = String(value).split("T")[0];
  const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return datePart;
  const [, y, m, d] = match;
  return `${d}-${m}-${y}`;
};

// ── Rounds a numeric value UP to at most 2 decimal places for display
const formatQty = (value) => {
  if (value === null || value === undefined || value === "") return value;
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return Math.ceil(num * 100) / 100;
};

// ── Formats a row's sales_order_numbers value into a single
//    comma-separated display string. The value is stored as a JSON
//    array string (e.g. '["SO001","SO002"]'), but this also tolerates
//    a plain comma string just in case.
const formatSalesOrders = (value) => {
  if (!value) return "-";
  let list = value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      list = Array.isArray(parsed) ? parsed : value.split(",");
    } catch (err) {
      list = value.split(",");
    }
  }
  if (!Array.isArray(list)) list = [list];
  const cleaned = list.map((v) => String(v).trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned.join(", ") : "-";
};

// ── Formats a row's production_order_number value into a plain,
//    comma-separated display string. A clubbed Goods Request line (same
//    Workcenter + Product Code) stores this as a JSON array string
//    (e.g. '["PRD0008","PRD0007"]'); an un-clubbed/legacy row is just a
//    plain string. Both are handled.
const formatProductionOrders = (value) => {
  if (!value) return "-";
  let list = value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      list = Array.isArray(parsed) ? parsed : value.split(",");
    } catch (err) {
      list = value.split(",");
    }
  }
  if (!Array.isArray(list)) list = [list];
  const cleaned = list.map((v) => String(v).trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned.join(", ") : "-";
};

const USER_MANUAL_SCOPE = {
    moduleName: "Logistics",
    process: "Outbound",
    subProcess: "Goods Issue"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

function GoodsIssue() {
  const location = useLocation();

  // Module-level access for this page — see getModulePermissions above.
  const { canView, canEdit } = React.useMemo(
    () => getModulePermissions("OUTBOUND_GOODS_ISSUE"),
    []
  );
  const canSeePage = canView || canEdit;

  // Field-level access for this module. Falls open (true) for admins,
  // users with no role assigned yet, or any non-DISPLAY permission type —
  // matching useFieldPermissions' own documented behavior.
  const { isFieldVisible } = useFieldPermissions("OUTBOUND_GOODS_ISSUE");

  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [selectedRows, setSelectedRows] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualExists, setManualExists] = useState(null);
  const [manualFile, setManualFile] = useState(null);
  const [issueItems, setIssueItems] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [batchOptions, setBatchOptions] = useState({});

  // ── Available Stock totals shown in the Create Goods Issue modal,
  //    keyed by row index — sum of stock qty for product_code + from-storage_code
  const [availableStock, setAvailableStock] = useState({});

  // ── "Available Stock" modal (opened from From Storage Location link) ──
  const [stockModalData, setStockModalData] = useState(null);
  const [stockModalLoading, setStockModalLoading] = useState(false);

  // ── View modal: header + Product tab (batch-level detail for one
  //    issue_no) + Docs tab (printable Material Issue slip) ─────────
  const [docsModal, setDocsModal] = useState(null); // { issueNo, items, activeDetailTab, autoPrint?, autoDownload?, previewOpen? }
  const [docsModalLoading, setDocsModalLoading] = useState(false);

  // ── Change Log tab data for the View modal ──────────────────────
  const [changeLogs, setChangeLogs] = useState([]);

  // Change Log — same pattern as GoodsRequest.js's fetchLogs(requestNo),
  // keyed here by issue_no.
  const fetchLogs = async (issueNo) => {
    try {
      const res = await axios.get(`${ORDER_API}/goods-issue/logs/${issueNo}`);
      if (res.data.success) {
        setChangeLogs(res.data.logs);
      }
    } catch (err) {
      console.error("Error fetching logs:", err);
    }
  };

  // ── Auto-print or auto-download when the preview modal opens with a flag ──
  useEffect(() => {
    if (docsModal?.autoPrint) {
      const timer = setTimeout(() => printGoodsIssue(), 1200);
      return () => clearTimeout(timer);
    }
    if (docsModal?.autoDownload) {
      const timer = setTimeout(() => {
        downloadGoodsIssue(`${docsModal.issueNo}.pdf`);
      }, 1200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docsModal]);

  // ── Opens the View modal for an issue_no: fetches every batch row
  //    recorded against that issue and shows the Product tab by default
  //    (the Docs tab renders the printable Material Issue slip).
  const openDetailModal = async (issueNo) => {
    try {
      setDocsModalLoading(true);

      const res = await axios.get(
        `${ORDER_API}/goods-issue/issued/${issueNo}`
      );

      setDocsModal({
        issueNo,
        items: res.data.data || [],
        activeDetailTab: "product",
      });

      fetchLogs(issueNo);
    } catch (err) {
      console.error("Failed to load issue details:", err);
      toast.error("Failed to load issue details");
    } finally {
      setDocsModalLoading(false);
    }
  };

  // ── Downloads the rendered slip as a PDF file using html2canvas + jsPDF
  const downloadGoodsIssue = async (filename) => {
    const printArea = document.getElementById("gi-print-area");
    if (!printArea) return;

    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"),
        import("jspdf"),
      ]);

      const canvas = await html2canvas(printArea, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20; // 10mm margin each side
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let yOffset = 10;
      let remaining = imgHeight;

      while (remaining > 0) {
        pdf.addImage(imgData, "PNG", 10, yOffset, imgWidth,
          Math.min(remaining, pageHeight - 20));
        remaining -= (pageHeight - 20);
        if (remaining > 0) {
          pdf.addPage();
          yOffset = 10;
        }
      }

      pdf.save(filename || "GoodsIssue.pdf");
    } catch (err) {
      console.error("PDF download failed:", err);
      toast.error("PDF download failed. Please try Print instead.");
    }
  };

  // ── Prints by capturing the already-rendered slip into a hidden
  //    iframe — no new tab, no login redirect.
  const printGoodsIssue = () => {
    const printArea = document.getElementById("gi-print-area");
    if (!printArea) return;

    const styleSheets = Array.from(document.styleSheets)
      .map((ss) => {
        try {
          return Array.from(ss.cssRules || []).map((r) => r.cssText).join("\n");
        } catch { return ""; }
      })
      .join("\n");

    const iframe = document.createElement("iframe");
    iframe.style.cssText = "position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Goods Issue</title>
<style>${styleSheets}
body{background:#fff!important;margin:0;padding:16px;font-family:Arial,Helvetica,sans-serif;}
@page{size:A4;margin:14mm;}
</style></head><body>${printArea.outerHTML}</body></html>`);
    doc.close();
    iframe.contentWindow.focus();
    setTimeout(() => {
      iframe.contentWindow.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 400);
  };

  // ── Filter dropdown option lists ──────────────────────────────
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [subCategoryOptions, setSubCategoryOptions] = useState([]);
  const [workcenterOptions, setWorkcenterOptions] = useState([]);
  const [storageLocationOptions, setStorageLocationOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);

  // ── Active filter values ──────────────────────────────────────
  const [filters, setFilters] = useState({
    productType: null,
    category: null,
    subCategory: null,
    workcenter: null,
    storageLocation: null,
    productionOrder: null,
    fromDate: "",
    toDate: "",
    productCode: null,
    description: null,
  });

  useEffect(() => {

    if (activeTab === "active") {

      fetchRequestedData(filters.fromDate, filters.toDate);

    } else {

      fetchIssuedData(filters.fromDate, filters.toDate);

    }

  }, [activeTab, filters.fromDate, filters.toDate]);
  useEffect(() => {

    fetchStorageLocations();
    fetchDropdownOptions();
    fetchProductOptions();

  }, []);

  const fetchDropdownOptions = async () => {
    try {
      const [productTypes, categories, subCategories, workcenters] =
        await Promise.all([
          axios.get(`${MASTER_API}/configuration/product-types`),
          axios.get(`${MASTER_API}/configuration/category`),
          axios.get(`${MASTER_API}/configuration/sub-category`),
          axios.get(`${ORDER_API}/goods-issue/workcenters`),
        ]);

      setProductTypeOptions(
        (productTypes.data || []).map((r) => ({
          value: r.name || r.id,
          label: r.name,
        }))
      );

      setCategoryOptions(
        (categories.data || []).map((r) => ({
          value: r.name || r.id,
          label: r.name,
        }))
      );

      setSubCategoryOptions(
        (subCategories.data || []).map((r) => ({
          value: r.name || r.id,
          label: r.name,
        }))
      );

      setWorkcenterOptions(
        (workcenters.data.data || []).map((r) => ({
          value: r.workcenter_name,
          label: r.workcenter_name,
        }))
      );
    } catch (err) {
      console.error("Failed to load filter options:", err);
    }
  };

  const fetchProductOptions = async () => {
    try {
      const res = await axios.get(`${ORDER_API}/goods-issue/product-options`);
      const rows = res.data.data || [];

      setProductOptions(
        rows.map((r) => ({
          value: r.productCode,
          label: `${r.productCode} - ${r.productDescription}`,
          description: r.productDescription,
        }))
      );
    } catch (err) {
      console.error("Failed to load product options:", err);
    }
  };

  const fetchBatches = async (productCode, storageCode) => {

    if (!productCode || !storageCode) return;

    try {

      const res = await axios.get(
        `${ORDER_API}/goods-issue/batches/${productCode}`,
        { params: { storageCode } }
      );

      setBatchOptions(prev => ({

        ...prev,

        [`${productCode}_${storageCode}`]: (res.data.data || []).filter(
          (b) => Number(b.qty) > 0
        )

      }));

    }
    catch (err) {

      console.log(err);

    }

  };

  // ── Fetches stock for a product_code + from-storage_code and stores
  //    the summed qty for that Create Goods Issue modal row (by index)
  const fetchAvailableStock = async (index, productCode, storageCode) => {

    if (!productCode || !storageCode) return;

    try {

      const res = await axios.get(
        `${ORDER_API}/goods-issue/stock-by-batch`,
        { params: { productCode, storageCode } }
      );

      const rows = res.data.data || [];

      const total = rows.reduce(
        (sum, r) => sum + (Number(r.qty) || 0),
        0
      );

      setAvailableStock((prev) => ({
        ...prev,
        [index]: total,
      }));

    } catch (err) {

      console.log(err);

    }

  };

  // ── Opens the Available Stock modal for a "From Storage Location" link,
  //    fetching batch_number + qty from the stock table for this product
  //    (and batch, when one is already known on the row).
  const openStockModal = async (productCode, description, storageLabel, storageCode) => {
    setStockModalData({
      product_code: productCode,
      description: description,
      storage_label: storageLabel,
      batches: [],
    });
    setStockModalLoading(true);

    try {
      const res = await axios.get(
        `${ORDER_API}/goods-issue/stock-by-batch`,
        { params: { productCode, storageCode } }
      );

      setStockModalData((prev) =>
        prev && prev.product_code === productCode
          ? { ...prev, batches: res.data.data || [] }
          : prev
      );
    } catch (err) {
      console.log(err);
    } finally {
      setStockModalLoading(false);
    }
  };

  const fetchRequestedData = async (fromDate, toDate) => {
    try {
      const res = await axios.get(
        `${ORDER_API}/goods-issue/list`,
        { params: { fromDate, toDate } }
      );
      setData(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };
  const handleSelect = (item) => {
    const exists = selectedRows.find(
      x =>
        x.production_order_number === item.production_order_number &&
        x.product_code === item.product_code
    );

    if (exists) {
      setSelectedRows(
        selectedRows.filter(
          x =>
            !(
              x.production_order_number === item.production_order_number &&
              x.product_code === item.product_code
            )
        )
      );
    } else {
      setSelectedRows([...selectedRows, item]);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRows(filteredData);
    } else {
      setSelectedRows([]);
    }
  };

  // ── Derive unique Production Order / Product Code / Description
  //    options from the fetched table data (no extra API call needed)
  const productionOrderOptions = useMemo(
    () =>
      [...new Set((data || []).map((r) => r.production_order_number).filter(Boolean))].map(
        (v) => ({ value: v, label: v })
      ),
    [data]
  );

  // ── Client-side filter applied to table rows ──────────────────
  const filteredData = useMemo(() => {
    return (data || []).filter((item) => {
      if (
        filters.productionOrder &&
        item.production_order_number !== filters.productionOrder.value
      )
        return false;

      if (filters.productCode && item.product_code !== filters.productCode.value)
        return false;

      if (filters.description && item.description !== filters.description.value)
        return false;

      if (filters.workcenter && item.workcenter_name !== filters.workcenter.value)
        return false;

      if (filters.productType && item.product_type_name !== filters.productType.value)
        return false;

      if (filters.category && item.category_name !== filters.category.value)
        return false;

      if (filters.subCategory && item.sub_category_name !== filters.subCategory.value)
        return false;

      if (
        filters.storageLocation &&
        item.storage_code !== filters.storageLocation.value
      )
        return false;

      return true;
    });
  }, [data, filters]);

  const isAllSelected =
    filteredData.length > 0 &&
    filteredData.every((item) =>
      selectedRows.some(
        (x) =>
          x.production_order_number === item.production_order_number &&
          x.product_code === item.product_code
      )
    );

  const fetchStorageLocations = async () => {

    try {

      const res = await axios.get(
        `${ORDER_API}/goods-issue/storage-locations`
      );

      const rows = res.data.data || [];

      setStorageLocations(rows);

      setStorageLocationOptions(
        rows.map((r) => ({
          value: r.storage_code,
          label: `${r.storage_code} - ${r.storage_location}`,
          storage_location: r.storage_location,
        }))
      );

    } catch (err) {

      console.log(err);

    }

  };
  const handleBatchSelectionChange = (itemIndex, selectedOptions) => {
    const temp = [...issueItems];
    const existing = temp[itemIndex].batches || [];
    const product_code = temp[itemIndex].product_code;
    const storage_code = temp[itemIndex].storage_code;
    const available = batchOptions[`${product_code}_${storage_code}`] || [];

    const newBatches = (selectedOptions || []).map((opt) => {
      const already = existing.find((b) => b.batch_number === opt.value);
      if (already) return already;

      const stockBatch = available.find((x) => x.batch_number === opt.value);
      return {
        batch_number: opt.value,
        issue_qty: stockBatch ? stockBatch.qty : "",
      };
    });

    temp[itemIndex].batches = newBatches;
    setIssueItems(temp);
  };

  const getTotalIssueQty = (item) =>
    (item.batches || []).reduce(
      (sum, b) => sum + (Number(b.issue_qty) || 0),
      0
    );

  const handleTotalQtyChange = (itemIndex, newTotalValue) => {
    const temp = [...issueItems];
    const item = temp[itemIndex];
    const batches = item.batches || [];

    if (batches.length === 0) {
      setIssueItems(temp);
      return;
    }

    const newTotal = Number(newTotalValue) || 0;
    const product_code = item.product_code;
    const storage_code = item.storage_code;
    const available = batchOptions[`${product_code}_${storage_code}`] || [];

    // Greedy / FIFO fill: consume each selected batch fully (up to its
    // available stock) in the order they were selected, then spill the
    // remainder into the next batch, and so on.
    let remaining = newTotal;

    batches.forEach((b) => {
      const stockBatch = available.find(
        (x) => x.batch_number === b.batch_number
      );
      const maxQty = stockBatch ? Number(stockBatch.qty) || 0 : Infinity;

      const take = Math.max(0, Math.min(remaining, maxQty));
      b.issue_qty = Number(take.toFixed(4));
      remaining = Number((remaining - take).toFixed(4));
    });

    // If remaining > 0, the selected batches don't have enough combined
    // stock to cover the requested total — surface that instead of
    // silently under-issuing.
    if (remaining > 0) {
      toast.warning(
        `Selected batches only have enough stock to cover ${(
          newTotal - remaining
        ).toFixed(4)} of the requested ${newTotal}. Select additional batches.`
      );
    }

    item.batches = batches;
    setIssueItems(temp);
  };
  const openIssueModal = () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one row");
      return;
    }

    const temp = selectedRows.map((item) => ({
      ...item,
      to_storage_code: item.storage_code,
      to_storage_location: item.storage_location,
      issue_date: getLocalISODate(),

      storage_code:
        storageLocations.length > 0
          ? storageLocations[0].storage_code
          : "",

      storage_location:
        storageLocations.length > 0
          ? storageLocations[0].storage_location
          : "",

      batches: []
    }));

    setIssueItems(temp);
    setAvailableStock({});
    temp.forEach((item, index) => {

      fetchBatches(item.product_code, item.storage_code);
      fetchAvailableStock(index, item.product_code, item.storage_code);

    });
    setShowModal(true);
  };
  // ── Closes a goods request without issuing any material. Only
  //    allowed while nothing has been issued against it yet (backend
  //    re-validates this too). Row moves from Active into Issued tab
  //    with status "Closed".
  const handleCloseRequest = async (item) => {

    if (!window.confirm(
      `Close this request for ${item.product_code}? This cannot be undone.`
    )) {
      return;
    }

    try {

      await axios.post(
        `${ORDER_API}/goods-issue/close/${item.id}`,
        {
          changedBy: (() => {
            try { return JSON.parse(localStorage.getItem("user") || "{}")?.email || ""; } catch { return ""; }
          })(),
        }
      );

      toast.success("Request Closed Successfully");

      fetchRequestedData(filters.fromDate, filters.toDate);

    } catch (err) {

      console.log(err);

      toast.error(
        err?.response?.data?.message || "Failed to close request"
      );

    }

  };

  const fetchIssuedData = async (fromDate, toDate) => {
    try {
      const res = await axios.get(
        `${ORDER_API}/goods-issue/issued`,
        { params: { fromDate, toDate } }
      );
      setData(res.data.data);
    } catch (err) {
      console.log(err);
    }
  };
  const styles = {

    modalBackdrop: {
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.45)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 1050,
    },

    modal: {
      background: "#fff",
      borderRadius: "8px",
      maxHeight: "85vh",
      width: "95%",
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },

    modalHeader: {
      padding: "12px 20px",
      borderBottom: "1px solid #ddd",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexShrink: 0,
    },

    modalBody: {
      padding: "20px",
      overflow: "auto",
      flex: 1,
    },

    modalFooter: {
      padding: "12px 20px",
      borderTop: "1px solid #ddd",
      display: "flex",
      justifyContent: "flex-end",
      gap: "10px",
    },

    iconBtn: {
      background: "transparent",
      border: "none",
      fontSize: "16px",
      cursor: "pointer",
    },

    compactInput: {
      fontSize: "13px",
      height: "32px",
      padding: "4px 8px",
    },

    filterCard: {
      background: "#fff",
      borderRadius: "8px",
      padding: "15px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      marginBottom: "15px",
    },

    compactLabel: {
      fontSize: "12px",
      fontWeight: "600",
      color: "#334155",
      marginBottom: "4px",
      display: "block",
    },

    pageTitleBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 15,
    },

    pageTitle: {
      fontSize: "22px",
      fontWeight: "700",
      color: "#1e293b",
    },

    pageSubtitle: {
      fontSize: "13px",
      color: "#64748b",
    },

    tableContainer: {
      background: "#fff",
      borderRadius: 8,
      padding: 10,
      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      overflowX: "auto",
    },

    erpTable: {
      width: "100%",
      minWidth: "1400px",
      borderCollapse: "collapse",
    },

    erpTh: {
      background: "#f8fafc",
      fontSize: 12,
      fontWeight: 600,
      padding: 10,
      borderBottom: "1px solid #e5e7eb",
      color: "#1e293b",
      whiteSpace: "nowrap",
    },

    erpTd: {
      fontSize: 13,
      padding: 10,
      borderBottom: "1px solid #f1f5f9",
      color: "#334155",
    },

    modalTd: {
      fontSize: 13,
      padding: 10,
      borderBottom: "1px solid #f1f5f9",
      color: "#334155",
      whiteSpace: "nowrap",
      verticalAlign: "middle",
    },

    modalInput: {
      width: "130px",
      height: "36px",
      fontSize: "13px",
    },

    cellLink: {
      color: "#2563eb",
      textDecoration: "none",
      fontWeight: 600,
      cursor: "pointer",
    },

    readOnlyBox: {
      fontSize: "13px",
      color: "#1e293b",
      background: "#f8fafc",
      border: "1px solid #e2e8f0",
      borderRadius: "6px",
      padding: "6px 10px",
      minHeight: "32px",
      display: "flex",
      alignItems: "center",
    },
  };

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      height: "38px",
      fontSize: "13px",
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "38px",
      padding: "0 8px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: "38px",
    }),
    option: (provided) => ({
      ...provided,
      fontSize: "12px",
      padding: "6px 12px",
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: "12px",
    }),
  };

  const modalSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "36px",
      fontSize: "13px",
      minWidth: "220px",
      borderRadius: "6px",
      borderColor: "#ced4da",
      boxShadow: "none",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 8px",
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      padding: "0 4px",
    }),
    indicatorSeparator: () => ({ display: "none" }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 2000,
    }),
    option: (provided) => ({
      ...provided,
      fontSize: "12px",
    }),
  };


  const handleSaveIssue = async () => {

    try {
      for (const item of issueItems) {

        if (!item.batches || item.batches.length === 0) {
          toast.warning(`Please select at least one batch for ${item.product_code}`);
          return;
        }

        const hasEmptyQty = item.batches.some(
          (b) => !b.issue_qty || Number(b.issue_qty) <= 0
        );

        if (hasEmptyQty) {
          toast.warning(`Please enter Issue Qty for all selected batches of ${item.product_code}`);
          return;
        }

        // ── Non-blocking warning: issuing more than the Requested Qty is
        //    allowed, but flag it to the user so an over-issue isn't
        //    accidental. This does NOT stop the save.
        const totalIssueQty = item.batches.reduce(
          (sum, b) => sum + (Number(b.issue_qty) || 0),
          0
        );
        if (Number(item.request_qty) > 0 && totalIssueQty > Number(item.request_qty)) {
          toast.warning(
            `Issue Qty (${formatQty(totalIssueQty)}) for ${item.product_code} exceeds Requested Qty (${formatQty(item.request_qty)})`
          );
        }

      }

      const currentUserId = getCurrentUserId();

      await axios.post(
        `${ORDER_API}/goods-issue/save`,
        {
          items: issueItems.map((item) => ({ ...item, created_by: currentUserId }))
        }
      );

      toast.success("Goods Issued Successfully");



      setShowModal(false);

      setSelectedRows([]);

      setActiveTab("active");

      fetchRequestedData(filters.fromDate, filters.toDate);

    } catch (err) {

      console.log(err);

      toast.error("Failed to save Goods Issue");

    }

  };

  // A non-admin user with no DISPLAY/CHANGE entry for this module (either
  // no role assigned at all, or a role that simply doesn't include Goods
  // Issue) has canView/canEdit both false, per getModulePermissions above
  // — same convention Confirmation, Goods Request, and Rework Order use.
  // Admins always pass via the explicit user.role === "admin" branch.
  if (!canSeePage) {
    return (
      <Layout>
        <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 14 }}>
          You don't have access to Goods Issue. Contact your administrator if you believe this is a mistake.
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.pageTitleBar}>
        <div>
          <h2 style={styles.pageTitle}>
            Goods Issue
          </h2>

          <p style={styles.pageSubtitle}>
            Goods Issue Management
          </p>
        </div>
        {/* --- User Manual Icon Button --- */}
        <button
          onClick={() => {
            setManualExists(null);
            setManualFile(null);
            setShowManual(true);
            fetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
              .then((res) => res.json())
              .then((data) => {
                const norm = (v) => String(v || "").trim().toLowerCase();
                const match = (data?.data || []).find((m) =>
                  norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                  norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                  norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                );
                if (match && match.drive_link) {
                  setManualFile(match);
                  setManualExists(true);
                } else {
                  setManualExists(false);
                }
              })
              .catch(() => setManualExists(false));
          }}
                title="View User Manual"
                    
                style={{
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    padding: "10px 14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    color: "#3b82f6",
                    fontSize: "14px",
                    fontWeight: "600",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
            >
                <FileText size={18} />
                User Manual
            </button>
      </div>

      {showManual && (
  <div
    onClick={() => setShowManual(false)}
    style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
      alignItems: "center", justifyContent: "center", zIndex: 1100,
    }}
  >
     <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: "#ffffff",
                            borderRadius: "12px",
                            width: "90vw",
                            height: "97vh",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "hidden",
                            boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                        }}
                    >
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
      }}>
        <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
          User Manual
        </h4>
        <button
          onClick={() => setShowManual(false)}
          style={{
            border: "none", background: "#f1f5f9", borderRadius: "8px",
            width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
            color: "#475569", lineHeight: 1,
          }}
          title="Close"
        >
          ×
        </button>
      </div>

      {manualExists === null && (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
          Checking for user manual...
        </div>
      )}

      {manualExists === false && (
        <div style={{
          flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
          justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
        }}>
          <FileText size={40} color="#cbd5e1" />
          <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
            User Manual Not Available
          </div>
          <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
            The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
          </div>
        </div>
      )}

      {manualExists === true && manualFile && (() => {
    const MANUAL_SCALE = 1.25;
    return (
        <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
            <iframe
                src={manualFile.drive_link.replace("/view", "/preview")}
                title={manualFile.file_name || "User Manual"}
                allow="autoplay"
                style={{
                    border: "none",
                    width: `${100 / MANUAL_SCALE}%`,
                    height: `${100 / MANUAL_SCALE}%`,
                    transform: `scale(${MANUAL_SCALE})`,
                    transformOrigin: "top left",
                }}
            />
        </div>
    );
})()}
    </div>
  </div>
)}


      {/* <div style={styles.filterCard}>

        <div className="row g-3">
          <div className="col-md-3">
            <label style={styles.compactLabel}>Product Type</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={productTypeOptions}
              value={filters.productType}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, productType: val }))
              }
              isClearable
            />
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col">
            <label style={styles.compactLabel}>Category</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={categoryOptions}
              value={filters.category}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, category: val }))
              }
              isClearable
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>Sub Category</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={subCategoryOptions}
              value={filters.subCategory}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, subCategory: val }))
              }
              isClearable
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>Workcenter</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={workcenterOptions}
              value={filters.workcenter}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, workcenter: val }))
              }
              isClearable
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>Storage Location</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={storageLocationOptions}
              value={filters.storageLocation}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, storageLocation: val }))
              }
              isClearable
            />
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col">
            <label style={styles.compactLabel}>Production Order</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={productionOrderOptions}
              value={filters.productionOrder}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, productionOrder: val }))
              }
              isClearable
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>Product Code</label>
            <Select
              styles={selectStyles}
              placeholder="Search by code or description..."
              options={productOptions}
              value={filters.productCode}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, productCode: val }))
              }
              isClearable
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>From </label>
            <input
              type="date"
              className="form-control"
              style={styles.compactInput}
              value={filters.fromDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
              }
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>To </label>
            <input
              type="date"
              className="form-control"
              style={styles.compactInput}
              value={filters.toDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, toDate: e.target.value }))
              }
            />
          </div>
        </div>
      </div> */}

      <div style={styles.filterCard}>

        {/* Row 1 – Product Type */}
        <div className="row g-3">
          <div className="col-md-3">
            <label style={styles.compactLabel}>Product Type</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={productTypeOptions}
              value={filters.productType}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, productType: val }))
              }
              isClearable
            />
          </div>
        </div>

        {/* Row 2 – Category / Sub Category / Workcenter / Storage Location / Product Code / Production Order */}
        <div className="row g-3 mt-1">
          <div className="col">
            <label style={styles.compactLabel}>Category</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={categoryOptions}
              value={filters.category}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, category: val }))
              }
              isClearable
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>Sub Category</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={subCategoryOptions}
              value={filters.subCategory}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, subCategory: val }))
              }
              isClearable
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>Workcenter</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={workcenterOptions}
              value={filters.workcenter}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, workcenter: val }))
              }
              isClearable
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>Storage Location</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={storageLocationOptions}
              value={filters.storageLocation}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, storageLocation: val }))
              }
              isClearable
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>Product Code</label>
            <Select
              styles={selectStyles}
              placeholder="Search by code or description..."
              options={productOptions}
              value={filters.productCode}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, productCode: val }))
              }
              isClearable
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>Production Order</label>
            <Select
              styles={selectStyles}
              placeholder="Select..."
              options={productionOrderOptions}
              value={filters.productionOrder}
              onChange={(val) =>
                setFilters((prev) => ({ ...prev, productionOrder: val }))
              }
              isClearable
            />
          </div>
        </div>

        {/* Row 3 – From Date / To Date (commented out)
        <div className="row g-3 mt-1">
          <div className="col">
            <label style={styles.compactLabel}>From </label>
            <input
              type="date"
              className="form-control"
              style={styles.compactInput}
              value={filters.fromDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, fromDate: e.target.value }))
              }
            />
          </div>

          <div className="col">
            <label style={styles.compactLabel}>To </label>
            <input
              type="date"
              className="form-control"
              style={styles.compactInput}
              value={filters.toDate}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, toDate: e.target.value }))
              }
            />
          </div>
        </div>
        */}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px"
        }}
      >

        <div className="gr-tab-toggle">

          <button
            className={
              activeTab === "active"
                ? "active-tab"
                : ""
            }
            onClick={() => setActiveTab("active")}
          >
            Active
          </button>

          <button
            className={
              activeTab === "issued"
                ? "active-tab"
                : ""
            }
            onClick={() => setActiveTab("issued")}
          >
            Issued
          </button>

        </div>

        {
          activeTab === "active" && canEdit && (

            <button
              className="btn btn-dark"
              onClick={openIssueModal}
            >
              Issue
            </button>

          )
        }

      </div>

      <div style={styles.tableContainer}>
        <table
          className="table"
          style={styles.erpTable}
        >
          <thead>
            <tr>

              {activeTab === "active" && canEdit && (
                <th style={styles.erpTh}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />{" "}
                </th>
              )}

              {activeTab === "issued" && (
                <th style={styles.erpTh}>
                  Issue No
                </th>
              )}

              <th style={styles.erpTh}>
                Production Order
              </th>

              <th style={styles.erpTh}>
                Sales Order
              </th>

              <th style={styles.erpTh}>
                Workcenter
              </th>

              {/* <th style={styles.erpTh}>
                Start Date
              </th>

              <th style={styles.erpTh}>
                End Date
              </th> */}

              <th style={styles.erpTh}>
                Product Code
              </th>

              <th style={styles.erpTh}>
                Description
              </th>

              <th style={styles.erpTh}>
                UOM
              </th>

              <th style={styles.erpTh}>
               Actual Qty
              </th>

              <th style={styles.erpTh}>
                Requested Qty
              </th>

              {activeTab === "active" && (
                <th style={styles.erpTh}>
                  Open Qty
                </th>
              )}

              <th style={styles.erpTh}>
                Requested Date
              </th>

              <th style={styles.erpTh}>
                To Storage Location
              </th>

              {activeTab === "issued" && (
                <>
                  <th style={styles.erpTh}>
                    From Storage Location
                  </th>

                  <th style={styles.erpTh}>
                    Available Stock
                  </th>

                  <th style={styles.erpTh}>
                    Batch Number
                  </th>

                  <th style={styles.erpTh}>
                    Issued Qty
                  </th>

                  <th style={styles.erpTh}>
                    Issued Date
                  </th>

                  <th style={styles.erpTh}>
                    Status
                  </th>

                  <th style={styles.erpTh}>
                    View
                  </th>
                </>
              )}

              {activeTab === "active" && canEdit && (
                <th style={styles.erpTh}>
                </th>
              )}

            </tr>
          </thead>

          <tbody>

            {filteredData.length > 0 ? (

              filteredData.map((item, index) => (

                <tr key={index}>

                  {activeTab === "active" && canEdit && (
                    <td style={styles.erpTd}>
                      <input
                        type="checkbox"
                        checked={selectedRows.some(
                          x =>
                            x.production_order_number ===
                            item.production_order_number &&
                            x.product_code ===
                            item.product_code
                        )}
                        onChange={() => handleSelect(item)}
                      />
                    </td>
                  )}

                  {activeTab === "issued" && (
                    <td style={styles.erpTd}>
                      {!isFieldVisible('issue_no') ? <MaskedValue /> : item.issue_no}
                    </td>
                  )}

                  <td style={styles.erpTd}>
                    {!isFieldVisible('production_order_number') ? <MaskedValue /> : formatProductionOrders(item.production_order_number)}
                  </td>

                  <td style={styles.erpTd}>
                    {!isFieldVisible('sales_order_numbers') ? <MaskedValue /> : formatSalesOrders(item.sales_order_numbers)}
                  </td>

                  <td style={styles.erpTd}>
                    {!isFieldVisible('workcenter_name') ? <MaskedValue /> : (
                      <Link
                        to={`/cable/master-data/workcenter?view=${encodeURIComponent(
                          item.workcenter_name
                        )}&returnTo=${encodeURIComponent(
                          location.pathname + location.search
                        )}`}
                        style={styles.cellLink}
                      >
                        {item.workcenter_name}
                      </Link>
                    )}
                  </td>

                  {/* <td style={styles.erpTd}>
                    {toDMY(item.production_start_date)}
                  </td>

                  <td style={styles.erpTd}>
                    {toDMY(item.production_end_date)}
                  </td> */}

                  <td style={styles.erpTd}>
                    {!isFieldVisible('product_code') ? <MaskedValue /> : (
                      <Link
                        to={`/cable/product/product-code?view=${encodeURIComponent(
                          item.product_code
                        )}&returnTo=${encodeURIComponent(
                          location.pathname + location.search
                        )}`}
                        style={styles.cellLink}
                      >
                        {item.product_code}
                      </Link>
                    )}
                  </td>

                  <td style={styles.erpTd}>
                    {!isFieldVisible('description') ? <MaskedValue /> : item.description}
                  </td>

                  <td style={styles.erpTd}>
                    {!isFieldVisible('uom') ? <MaskedValue /> : item.uom}
                  </td>

                  <td style={styles.erpTd}>
                    {!isFieldVisible('qty') ? <MaskedValue /> : formatQty(item.qty)}
                  </td>

                  <td style={styles.erpTd}>
                    {!isFieldVisible('request_qty') ? <MaskedValue /> : formatQty(item.request_qty)}
                  </td>

                  {activeTab === "active" && (
                    <td style={styles.erpTd}>
                      {!isFieldVisible('open_qty') ? <MaskedValue /> : formatQty(item.open_qty)}
                    </td>
                  )}

                  <td style={styles.erpTd}>
                    {!isFieldVisible('request_date') ? <MaskedValue /> : toDMY(item.request_date)}
                  </td>

                  {activeTab === "active" ? (
                    <td style={styles.erpTd}>
                      {!isFieldVisible('storage_location') ? <MaskedValue /> : item.storage_location}
                    </td>
                  ) : (
                    <>
                      <td style={styles.erpTd}>
                        {!isFieldVisible('to_storage_location') ? <MaskedValue /> : item.to_storage_location}
                      </td>

                      <td style={styles.erpTd}>
                        {!isFieldVisible('storage_location') ? <MaskedValue /> : (
                          item.storage_location ? (
                            <span
                              style={styles.cellLink}
                              onClick={() =>
                                openStockModal(
                                  item.product_code,
                                  item.description,
                                  item.storage_location,
                                  item.storage_code
                                )
                              }
                            >
                              {item.storage_location}
                            </span>
                          ) : (
                            "-"
                          )
                        )}
                      </td>

                      <td style={styles.erpTd}>
                        {!isFieldVisible('stock_qty') ? <MaskedValue /> : (
                          item.stock_qty !== undefined && item.stock_qty !== null
                            ? formatQty(item.stock_qty)
                            : "-"
                        )}
                      </td>

                      <td style={styles.erpTd}>
                        {!isFieldVisible('batch_number') ? <MaskedValue /> : item.batch_number}
                      </td>

                      <td style={styles.erpTd}>
                        {!isFieldVisible('issue_qty') ? <MaskedValue /> : formatQty(item.issue_qty)}
                      </td>

                      <td style={styles.erpTd}>
                        {!isFieldVisible('issue_date') ? <MaskedValue /> : (toDMY(item.issue_date) || "-")}
                      </td>

                      <td style={styles.erpTd}>
                        {!isFieldVisible('status') ? <MaskedValue /> : (
                          item.status === "Closed" ? (
                            <span style={{ color: "#dc2626", fontWeight: 600 }}>
                              Closed
                            </span>
                          ) : (
                            <span style={{ color: "#16a34a", fontWeight: 600 }}>
                              Issued
                            </span>
                          )
                        )}
                      </td>

                      <td style={styles.erpTd}>
                        {item.issue_no ? (
                          <button
                            className="btn btn-sm btn-dark"
                            style={{ fontSize: "11px", padding: "2px 8px" }}
                            onClick={() => openDetailModal(item.issue_no)}
                          >
                            View
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </>
                  )}

                  {activeTab === "active" && canEdit && (
                    <td style={styles.erpTd}>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        style={{ fontSize: "11px", padding: "2px 8px" }}
                        onClick={() => handleCloseRequest(item)}
                      >
                        Close
                      </button>
                    </td>
                  )}

                </tr>

              ))

            ) : (

              <tr>
                <td
                  colSpan={activeTab === "active" ? "15" : "20"}
                  className="text-center"
                >
                  No Records Found
                </td>
              </tr>

            )}

          </tbody>
        </table>
      </div>
      {showModal && (
        <div style={styles.modalBackdrop}>
          <div
            style={{
              ...styles.modal,
              maxWidth: "1300px",
            }}
          >
            {/* Header */}

            <div style={styles.modalHeader}>
              <h3
                style={{
                  margin: 0,
                  color: "#1e293b",
                  fontSize: "18px",
                  fontWeight: 600,
                }}
              >
                Goods Issue
              </h3>

              <button
                onClick={() => setShowModal(false)}
                style={styles.iconBtn}
              >
                ✖
              </button>
            </div>

            {/* Body */}

            <div style={styles.modalBody}>
              <div className="card p-3 mb-3 bg-light border-0" style={{ overflowX: "auto" }}>
                <table
                  className="table"
                  style={{ ...styles.erpTable, minWidth: "1550px" }}
                >
                  <thead>
                    <tr>
                      <th style={styles.erpTh}>Production Order</th>
                      <th style={styles.erpTh}>Sales Order</th>
                      <th style={styles.erpTh}>Workcenter</th>
                      {/* <th style={styles.erpTh}>Start Date</th> */}
                      <th style={styles.erpTh}>Product Code</th>
                      <th style={styles.erpTh}>Description</th>
                      <th style={styles.erpTh}>UOM</th>
                      <th style={styles.erpTh}>Actual Qty</th>
                      <th style={styles.erpTh}>Requested Qty</th>
                      <th style={styles.erpTh}>Requested Date</th>
                      <th style={styles.erpTh}>To Storage Location</th>
                      <th style={styles.erpTh}>From Storage Location</th>
                      <th style={styles.erpTh}>Available Stock</th>
                      <th style={styles.erpTh}>Batch Number</th>
                      <th style={styles.erpTh}>Issue Qty</th>
                      <th style={styles.erpTh}>
                        Issue Date
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {issueItems.map((item, index) => (
                      <tr key={index}>
                        <td style={styles.modalTd}>
                          {formatProductionOrders(item.production_order_number)}
                        </td>

                        <td style={styles.modalTd}>
                          {formatSalesOrders(item.sales_order_numbers)}
                        </td>

                        <td style={styles.modalTd}>
                          <Link
                            to={`/cable/master-data/workcenter?view=${encodeURIComponent(
                              item.workcenter_name
                            )}&returnTo=${encodeURIComponent(
                              location.pathname + location.search
                            )}`}
                            style={styles.cellLink}
                          >
                            {item.workcenter_name}
                          </Link>
                        </td>

                        {/* <td style={styles.modalTd}>
                          {toDMY(item.production_start_date)}
                        </td> */}

                        <td style={styles.modalTd}>
                          <Link
                            to={`/cable/product/product-code?view=${encodeURIComponent(
                              item.product_code
                            )}&returnTo=${encodeURIComponent(
                              location.pathname + location.search
                            )}`}
                            style={styles.cellLink}
                          >
                            {item.product_code}
                          </Link>
                        </td>

                        <td style={styles.modalTd}>
                          {item.description}
                        </td>

                        <td style={styles.modalTd}>
                          {item.uom}
                        </td>

                        <td style={styles.modalTd}>
                          {formatQty(item.qty)}
                        </td>

                        <td style={styles.modalTd}>
                          {item.request_qty}
                        </td>

                        <td style={styles.modalTd}>
                          {toDMY(item.request_date)}
                        </td>

                        <td style={styles.modalTd}>
                          {item.to_storage_location || ""}
                        </td>

                        <td style={styles.modalTd}>
                          <select
                            className="form-select"
                            style={{
                              width: "200px",
                              height: "36px",
                              fontSize: "13px",
                              padding: "4px 8px",
                            }}
                            value={item.storage_code || ""}
                            onChange={(e) => {
                              const temp = [...issueItems];

                              const selected = storageLocations.find(
                                (x) => x.storage_code === e.target.value
                              );

                              if (!selected) return;

                              temp[index].storage_code = selected.storage_code;
                              temp[index].storage_location = selected.storage_location;

                              // Batches picked under the old storage
                              // location are no longer valid here —
                              // clear them instead of leaving stale
                              // batch numbers on the row.
                              temp[index].batches = [];

                              setIssueItems(temp);
                              fetchAvailableStock(
                                index,
                                temp[index].product_code,
                                selected.storage_code
                              );
                              fetchBatches(
                                temp[index].product_code,
                                selected.storage_code
                              );
                            }}
                          >
                            <option value="" disabled>
                              Select
                            </option>
                            {storageLocations.map((loc) => (
                              <option
                                key={loc.storage_code}
                                value={loc.storage_code}
                              >
                                {loc.storage_code} - {loc.storage_location}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td style={styles.modalTd}>
                          {item.storage_code ? (
                            <span
                              style={styles.cellLink}
                              onClick={() =>
                                openStockModal(
                                  item.product_code,
                                  item.description,
                                  `${item.storage_code} - ${item.storage_location}`,
                                  item.storage_code
                                )
                              }
                            >
                              {availableStock[index] !== undefined
                                ? formatQty(availableStock[index])
                                : "..."}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td
                          style={{
                            minWidth: "240px",
                            verticalAlign: "middle",
                            whiteSpace: "normal",
                            padding: 10,
                            borderBottom: "1px solid #f1f5f9",
                          }}
                        >

                          <Select
                            isMulti
                            styles={modalSelectStyles}
                            placeholder="Select Batch(es)"
                            options={(batchOptions[`${item.product_code}_${item.storage_code}`] || []).map((x) => ({
                              value: x.batch_number,
                              label: `${x.batch_number} - ${x.qty}`,
                            }))}
                            value={(item.batches || []).map((b) => ({
                              value: b.batch_number,
                              label:
                                (batchOptions[`${item.product_code}_${item.storage_code}`] || []).find(
                                  (x) => x.batch_number === b.batch_number
                                )
                                  ? `${b.batch_number} - ${(batchOptions[`${item.product_code}_${item.storage_code}`] || []).find(
                                    (x) => x.batch_number === b.batch_number
                                  ).qty
                                  }`
                                  : b.batch_number,
                            }))}
                            onChange={(selectedOptions) =>
                              handleBatchSelectionChange(index, selectedOptions)
                            }
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />

                        </td>

                        <td style={styles.modalTd}>
                          <input
                            type="number"
                            className="form-control"
                            style={{
                              width: "100px",
                              height: "36px",
                              fontSize: "13px",
                              textAlign: "center",
                            }}
                            placeholder="Issue Qty"
                            disabled={!item.batches || item.batches.length === 0}
                            value={item.batches && item.batches.length > 0 ? getTotalIssueQty(item) : ""}
                            onChange={(e) => {
                              handleTotalQtyChange(index, e.target.value);
                            }}
                          />
                        </td>

                        <td style={styles.modalTd}>
                          <input
                            type="date"
                            className="form-control"
                            style={styles.modalInput}
                            value={item.issue_date}
                            onChange={(e) => {
                              const temp = [...issueItems];
                              temp[index].issue_date = e.target.value;
                              setIssueItems(temp);
                            }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Footer */}

            <div style={styles.modalFooter}>
              <button
                className="btn btn-outline-secondary"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>

              <button
                className="btn btn-dark"
                onClick={handleSaveIssue}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Available Stock Modal (From Storage Location) ──── */}
      {stockModalData && (
        <div
          style={{ ...styles.modalBackdrop, zIndex: 1100 }}
          onClick={() => setStockModalData(null)}
        >
          <div
            style={{ ...styles.modal, maxWidth: "480px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "#1e293b",
                  }}
                >
                  {stockModalData.product_code} - {stockModalData.description}
                </p>
                {stockModalData.storage_label && (
                  <p
                    style={{
                      margin: "2px 0 0",
                      fontSize: "12px",
                      color: "#64748b",
                    }}
                  >
                    {stockModalData.storage_label}
                  </p>
                )}
              </div>
              <button
                onClick={() => setStockModalData(null)}
                style={styles.iconBtn}
              >
                ✖
              </button>
            </div>

            <div style={styles.modalBody}>
              {stockModalLoading ? (
                <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                  Loading...
                </p>
              ) : stockModalData.batches.filter((b) => Number(b.qty) > 0).length > 0 ? (
                <table className="table" style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={styles.erpTh}>Batch No</th>
                      <th style={styles.erpTh}>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockModalData.batches
                      .filter((b) => Number(b.qty) > 0)
                      .map((b, i) => (
                        <tr key={i}>
                          <td style={styles.erpTd}>{b.batch_number || "-"}</td>
                          <td style={styles.erpTd}>{formatQty(b.qty)}</td>
                        </tr>
                      ))}
                    <tr>
                      <td style={{ ...styles.erpTd, fontWeight: 700 }}>Total</td>
                      <td style={{ ...styles.erpTd, fontWeight: 700 }}>
                        {formatQty(
                          stockModalData.batches.reduce(
                            (sum, b) => sum + Number(b.qty || 0),
                            0
                          )
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p style={{ fontSize: 13, color: "#64748b", margin: 0 }}>
                  No stock available.
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Goods Issue View Modal (header + Product/Docs tabs) ──── */}
      {docsModal && (
        <div
          style={{ ...styles.modalBackdrop, zIndex: 1200 }}
          onClick={() => setDocsModal(null)}
        >
          <div
            style={{ ...styles.modal, maxWidth: "1100px", width: "95%", height: "90vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <p style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>
                Issue : {docsModal.issueNo}
              </p>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <button onClick={() => setDocsModal(null)} style={styles.iconBtn}>✖</button>
              </div>
            </div>

            <div style={{ padding: "16px 20px 0" }}>
              {/* ── Header fields (fixed, same across tabs) ─────── */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(6, 1fr)",
                  gap: "16px",
                  marginBottom: "16px",
                }}
              >
                <div>
                  <label style={styles.compactLabel}>Issue No</label>
                  <div style={styles.readOnlyBox}>{docsModal.issueNo}</div>
                </div>
                <div>
                  <label style={styles.compactLabel}>Production Order</label>
                  <div style={styles.readOnlyBox}>
                    {formatProductionOrders(docsModal.items?.[0]?.production_order_number)}
                  </div>
                </div>
                <div>
                  <label style={styles.compactLabel}>Sales Order</label>
                  <div style={styles.readOnlyBox}>
                    {formatSalesOrders(docsModal.items?.[0]?.sales_order_numbers)}
                  </div>
                </div>
                <div>
                  <label style={styles.compactLabel}>Workcenter</label>
                  <div style={styles.readOnlyBox}>
                    {docsModal.items?.[0]?.workcenter_name || "-"}
                  </div>
                </div>
                {/* <div>
                  <label style={styles.compactLabel}>Start Date</label>
                  <div style={styles.readOnlyBox}>
                    {toDMY(docsModal.items?.[0]?.production_start_date) || "-"}
                  </div>
                </div>
                <div>
                  <label style={styles.compactLabel}>End Date</label>
                  <div style={styles.readOnlyBox}>
                    {toDMY(docsModal.items?.[0]?.production_end_date) || "-"}
                  </div>
                </div> */}
              </div>

              {/* ── Tab toggle: Product / Docs ──────────────────── */}
              <div className="gr-tab-toggle" style={{ marginBottom: "12px" }}>
                <button
                  className={docsModal.activeDetailTab === "product" ? "active-tab" : ""}
                  onClick={() =>
                    setDocsModal((prev) => ({ ...prev, activeDetailTab: "product" }))
                  }
                >
                  Product
                </button>
                <button
                  className={docsModal.activeDetailTab === "docs" ? "active-tab" : ""}
                  onClick={() =>
                    setDocsModal((prev) => ({ ...prev, activeDetailTab: "docs" }))
                  }
                >
                  Docs
                </button>
                <button
                  className={docsModal.activeDetailTab === "change_log" ? "active-tab" : ""}
                  onClick={() =>
                    setDocsModal((prev) => ({ ...prev, activeDetailTab: "change_log" }))
                  }
                >
                  Change Log
                </button>
              </div>
            </div>

            <div style={{ flex: 1, overflowY: "auto", padding: "0 20px 20px" }}>
              {/* ── Product tab: batch-level line-item table ────── */}
              {docsModal.activeDetailTab === "product" && (
                docsModalLoading ? (
                  <p style={{ fontSize: 13, color: "#64748b" }}>Loading...</p>
                ) : (
                  <table className="table" style={styles.erpTable}>
                    <thead>
                      <tr>
                        <th style={styles.erpTh}>Product Code</th>
                        <th style={styles.erpTh}>Description</th>
                        <th style={styles.erpTh}>UOM</th>
                        <th style={styles.erpTh}>Actual Qty</th>
                        <th style={styles.erpTh}>Requested Qty</th>
                        <th style={styles.erpTh}>Requested Date</th>
                        <th style={styles.erpTh}>To Storage Location</th>
                        <th style={styles.erpTh}>From Storage Location</th>
                        <th style={styles.erpTh}>Batch No</th>
                        <th style={styles.erpTh}>Issued Qty</th>
                        <th style={styles.erpTh}>Issued Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(docsModal.items || []).map((item, idx) => (
                        <tr key={item.batch_number || idx}>
                          <td style={styles.erpTd}>{item.product_code}</td>
                          <td style={styles.erpTd}>{item.description}</td>
                          <td style={styles.erpTd}>{item.uom}</td>
                          <td style={styles.erpTd}>{formatQty(item.qty)}</td>
                          <td style={styles.erpTd}>{formatQty(item.request_qty)}</td>
                          <td style={styles.erpTd}>{toDMY(item.request_date) || "-"}</td>
                          <td style={styles.erpTd}>{item.to_storage_location || "-"}</td>
                          <td style={styles.erpTd}>{item.storage_location || "-"}</td>
                          <td style={styles.erpTd}>{item.batch_number || "-"}</td>
                          <td style={styles.erpTd}>{formatQty(item.issue_qty)}</td>
                          <td style={styles.erpTd}>{toDMY(item.issue_date) || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )
              )}

              {/* ── Docs tab: file upload row + document card ──── */}
              {docsModal.activeDetailTab === "docs" && (
                <div>
                  <div style={{ display: "flex", gap: "0", marginBottom: "6px" }}>
                    <label
                      style={{
                        fontSize: "13px",
                        padding: "6px 14px",
                        background: "#e2e8f0",
                        border: "1px solid #cbd5e1",
                        borderRight: "none",
                        borderRadius: "6px 0 0 6px",
                        cursor: "pointer",
                        color: "#1e293b",
                      }}
                    >
                      Choose Files
                      <input
                        type="file"
                        style={{ display: "none" }}
                        onChange={(e) =>
                          setDocsModal((prev) => ({
                            ...prev,
                            chosenFileName: e.target.files?.[0]?.name || null,
                          }))
                        }
                      />
                    </label>
                    <div
                      style={{
                        fontSize: "13px",
                        padding: "6px 14px",
                        border: "1px solid #cbd5e1",
                        borderRadius: "0 6px 6px 0",
                        color: "#64748b",
                        flex: 1,
                      }}
                    >
                      {docsModal.chosenFileName || "No file chosen"}
                    </div>
                  </div>
                  <p style={{ fontSize: "12px", color: "#64748b", marginBottom: "16px" }}>
                    Supported formats: PDF, DOCX, XLSX
                  </p>

                  <div
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      padding: "14px 16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#1e293b",
                        marginBottom: "10px",
                      }}
                    >
                      <span>📄</span>
                      <span>{docsModal.issueNo}_GoodsIssue.pdf</span>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        className="btn btn-sm btn-dark"
                        onClick={() =>
                          setDocsModal((prev) => ({
                            ...prev,
                            previewOpen: true,
                            autoPrint: false,
                            autoDownload: false,
                          }))
                        }
                      >
                        View
                      </button>
                      <button
                        className="btn btn-sm btn-dark"
                        onClick={() =>
                          setDocsModal((prev) => ({
                            ...prev,
                            previewOpen: true,
                            autoDownload: true,
                            autoPrint: false,
                          }))
                        }
                      >
                        Download
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() =>
                          setDocsModal((prev) => ({
                            ...prev,
                            previewOpen: true,
                            autoPrint: true,
                            autoDownload: false,
                          }))
                        }
                      >
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Change Log tab: field-level audit trail ─────── */}
              {docsModal.activeDetailTab === "change_log" && (
                <table className="table" style={styles.erpTable}>
                  <thead>
                    <tr>
                      <th style={styles.erpTh}>Date & Time</th>
                      <th style={styles.erpTh}>User</th>
                      <th style={styles.erpTh}>Action</th>
                      <th style={styles.erpTh}>Field</th>
                      <th style={styles.erpTh}>Old Value</th>
                      <th style={styles.erpTh}>New Value</th>
                      <th style={styles.erpTh}>Comment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeLogs.length > 0 ? (
                      changeLogs.map((log, idx) => {
                        const dateVal = log.created_at || log.updated_at;
                        return (
                          <tr key={idx}>
                            <td style={styles.erpTd}>
                              {(() => {
                                const d = new Date(dateVal);
                                if (isNaN(d.getTime())) return "";
                                const dd = String(d.getDate()).padStart(2, "0");
                                const mm = String(d.getMonth() + 1).padStart(2, "0");
                                const yyyy = d.getFullYear();
                                const time = d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
                                return `${dd}/${mm}/${yyyy}, ${time}`;
                              })()}
                            </td>
                            <td style={styles.erpTd}>{log.created_by || log.updated_by || "System"}</td>
                            <td style={styles.erpTd}>{log.action_type || "Update"}</td>
                            <td style={styles.erpTd}>
                              {log.field_name
                                ? log.field_name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                : '-'}
                            </td>
                            <td style={{ ...styles.erpTd, maxWidth: 150 }}>{log.old_value || "-"}</td>
                            <td style={{ ...styles.erpTd, maxWidth: 150 }}>{log.new_value || "-"}</td>
                            <td style={{ ...styles.erpTd, maxWidth: 200, wordWrap: "break-word" }}>{log.comment || "-"}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" style={{ ...styles.erpTd, textAlign: "center", padding: 20 }}>
                          No changes recorded
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Full Slip Preview Modal (opened from the Docs tab's
              document card) ─────────────────────────────────── */}
      {docsModal?.previewOpen && (
        <div
          style={{ ...styles.modalBackdrop, zIndex: 1300 }}
          onClick={() =>
            setDocsModal((prev) => ({ ...prev, previewOpen: false }))
          }
        >
          <div
            style={{ ...styles.modal, maxWidth: "900px", width: "95%", height: "90vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <p
                style={{
                  margin: 0,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#1e293b",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <span>📄</span>
                <span>{docsModal.issueNo}_GoodsIssue.pdf</span>
              </p>
              <button
                className="btn btn-sm"
                style={{
                  fontSize: "12px",
                  color: "#dc2626",
                  border: "1px solid #dc2626",
                  background: "#fff",
                  borderRadius: "6px",
                  padding: "4px 12px",
                }}
                onClick={() =>
                  setDocsModal((prev) => ({ ...prev, previewOpen: false }))
                }
              >
                ✖ Close
              </button>
            </div>

            <div style={{ position: "relative", flex: 1, overflowY: "auto" }}>
              <button
                className="btn btn-sm btn-dark"
                style={{
                  position: "absolute",
                  top: "12px",
                  right: "20px",
                  fontSize: "12px",
                  zIndex: 2,
                }}
                onClick={() => downloadGoodsIssue(`${docsModal.issueNo}.pdf`)}
              >
                Download PDF
              </button>
              <div style={{ padding: "16px" }}>
                <GoodsIssueForm issueNo={docsModal.issueNo} />
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
      />

      <style>
        {`
.gr-tab-toggle{
display:inline-flex;
background:#f1f5f9;
border-radius:8px;
padding:3px;
gap:2px;
}

.gr-tab-toggle button{
border:none;
background:transparent;
padding:6px 18px;
font-size:13px;
font-weight:600;
border-radius:6px;
color:#64748b;
cursor:pointer;
transition:.2s;
}

.gr-tab-toggle button.active-tab{
background:#fff;
color:#1e293b;
box-shadow:0 1px 3px rgba(0,0,0,.15);
}
`}
      </style>

    </Layout>
  );
}

export default GoodsIssue;